import { createTheme } from "@mui/material/styles";
import { blue } from "@mui/material/colors";
import { deDE } from '@mui/x-date-pickers/locales';

declare module "@mui/material/styles" {
  interface Theme {
    topLevelBackground: string;
    topLevelPadding: string;
    mainHeader: string;
    cardBackground: string;
    lowLevelContainerBackground: string;
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    topLevelBackground?: string;
    topLevelPadding?: string;
    mainHeader?: string;
    cardBackground?: string;
    lowLevelContainerBackground?: string;
  }
}

// const blue1 = "#B7C6CD";
// const blue2 = "#87A2AD";
// const blue3 = "#3F6A7C";
// const blue4 = "#05445B";

export const cellSx = {
    fontSize: "1rem",
    borderRight: "1px solid",
    borderColor: "divider",
};

const blue1 = "#b2dbef";
const blue2 = "#6fa4bf";
const blue3 = "#367b9a";
const blue4 = "#06465D";

const generalBackground = "white"; //"#ECF2F5";

const TAB_HEIGHT = "0px";
// export const PATIENT_TAB_HEIGHT = TAB_HEIGHT;

const ELEVATION = 1;

const lowLevelBorderRadius = "6px";

/**
 * The app font stack. MUST stay in sync with the `body` rule in src/index.css.
 * See docs/design-system.md.
 *
 * This has to be passed to the FIRST createTheme() call, not a later merge.
 * MUI's createTypography() bakes fontFamily into every variant (body1, h1-h6,
 * button, caption, ...) at build time, then spreads the incoming typography
 * object OVER those variants. So merging `{typography: {fontFamily}}` onto an
 * already-built theme sets theme.typography.fontFamily but leaves every
 * variant on the old font — which silently splits the app in two: components
 * reading theme.typography.fontFamily (Chip, ...) switch, components reading a
 * variant (Typography, CssBaseline's body rule, ...) do not.
 */
const FONT_STACK = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  '"Roboto"',
  '"Oxygen"',
  '"Ubuntu"',
  '"Cantarell"',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  "sans-serif",
].join(",");

/**
 * Ink ramp — MIRROR of the --ink-* tokens in src/index.css. See
 * docs/design-system.md, rule 1 ("there are no grays"): every line in the UI is
 * the brand navy at low alpha, never a neutral gray.
 *
 * These have to be duplicated as literals rather than referencing the CSS
 * variables, because MUI runs color math on palette values —
 * `TableCell` does `lighten(alpha(theme.palette.divider, 1), 0.88)` — and
 * `alpha("var(--ink-line)")` throws "MUI: Unsupported color".
 *
 * mobiliTheme.test.ts asserts these stay in sync with index.css.
 */
const INK = "3, 48, 89";
const inkLine = `rgba(${INK}, 0.12)`; //  --ink-line        default border
const inkLineStrong = `rgba(${INK}, 0.14)`; //  --ink-line-strong primary rules
const inkFill = `rgba(${INK}, 0.38)`; //  filled control surfaces (switch track)

/** --surface-sunken: ink @ 0.066. The one recessed-surface value. */
const SURFACE_SUNKEN = "#EEF1F4";

// palette
export let mobiliTheme = createTheme({
  typography: { fontFamily: FONT_STACK },
  topLevelBackground: "#9EB1C0",
  topLevelPadding: "12px",
  mainHeader: "#003059",
  /**
   * Both of these are the --surface-sunken rung (ink @ 0.066). See
   * docs/design-system.md, "Surface ladder".
   *
   * cardBackground was #D9DFE2 — the brand navy at 0.16, so on-hue but 2.4x
   * the weight of the calendar's own canvas (0.066). That is why card content
   * read as a gray slab with white pills floating on it, while the calendar
   * reads as light. Content sits on white (rule 4); the recessed surface is
   * what the white sits ON, and one value is enough for the whole app.
   */
  cardBackground: SURFACE_SUNKEN,
  lowLevelContainerBackground: SURFACE_SUNKEN,
  palette: {
    /**
     * MUI's default divider is rgba(0, 0, 0, 0.12) — pure black, and it was
     * never overridden, so every Divider / table border / card outline /
     * outlined Paper in the app was neutral gray. Same 12% alpha, brand hue:
     * this is a straight de-graying, it does not change any border's weight.
     */
    divider: inkLine,
    primary: {
      main: "#003059",
      // light: blue1,
      // dark: blue3,
      //contrastText: "black",
    },
    secondary: {
      main: "#9C1A35",
    },
    success: {
      main: "#6FBF99",
    },
    info: {
      main: "#4361FF",
    },
    warning: {
      main: "#FFE80B",
    },
    error: {
      main: "#FF0000",
    },
  },
}, deDE);

export const mainAppGridStyle = {
  background: `linear-gradient(to top, ${mobiliTheme.mainHeader}, ${mobiliTheme.topLevelBackground})` /* Blue to light blue */,
};

export const RxStatusColors = {
  BEING_TREATED : "rgba(42, 168, 46, 1)",
  FINISHED : "#4361FF",
  BOOKED : "#000",
  INTERRUPTED : "#ff5722",
  HAS_ISSUES : "#FF0000",
};
export const attentionColors = {
  immediateAttention: "#ab003c",
  highPriority: mobiliTheme.palette.error.main,
  mediumPriority: "#ff5722",
  lowPriority: "#ffc107",
  customInfoPrimary: mobiliTheme.palette.info.main,
  // Add other colors as needed
};

// component overrides
mobiliTheme = createTheme(mobiliTheme, {
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          border: `0.5px solid ${mobiliTheme.mainHeader}`,
          borderRadius: "6px",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          textAlign: "left",
          /**
           * White, and LIGHTER than the container it caps — the same relationship
           * MobiliCalendar's own header has to its canvas
           * (`rgba(255,255,255,0.92)` over --surface-sunken). Chrome is the
           * lightest plane; separation comes from the rule below it, at the
           * calendar's hour-line weight, not from a fill contrast.
           *
           * A tinted header band was tried and reverted: it inverts the
           * calendar's relationship, and it is the enterprise-UI pattern
           * (Bootstrap .card-header, Jira) rather than the one contemporary
           * product UIs use. What the header had actually lost when the
           * container lightened was its RULE, not its contrast.
           *
           * FlatCard forces this transparent, so flat cards are unaffected.
           */
          backgroundColor: "white",
          borderBottom: "1px solid var(--ink-line-strong)",
          color: mobiliTheme.mainHeader,
          padding: "5px",
        },
        title: {
          // ABSOLUTE size on purpose: the previous "1.25em" scaled with
          // whatever font-size the header inherited, so cards that tweaked
          // their header font (dashboard digests) got 17.5px titles while
          // plain ones got 20px. One rem-based size = identical titles
          // everywhere.
          fontSize: "1.1rem",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.cardBackground,
          // color: mobiliTheme.palette.primary.contrastText,
          // padding:"4px",
          // paddingBottom: "4px",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          display: "flex",
          justifyContent: "flex-end",
          backgroundColor: mobiliTheme.cardBackground,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "0px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: mobiliTheme.palette.secondary.main,
            color: mobiliTheme.palette.secondary.contrastText,
          },
          // Unselected tabs keep the previous white background (primary is now dark).
          backgroundColor: "white",
          borderTopLeftRadius: "10px", // Top-left corner rounded
          borderTopRightRadius: "10px", // Top-right corner rounded
          borderBottomLeftRadius: "0px", // Bottom-left corner square
          borderBottomRightRadius: "0px", // Bottom-right corner square
          textTransform: "none",
          minHeight: TAB_HEIGHT,
          marginRight: "5px",
          paddingTop: "8px",
          paddingBottom: "8px",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: ELEVATION,
      },
      styleOverrides: {
        root: {
          //   backgroundColor: mobiliTheme.palette.primary.light,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        // elevation: ELEVATION,
        variant: "contained",
      },
      styleOverrides: {
        root: {
          // Contained ("paper"/elevated) buttons: white surface with the brand-blue
          // label. Scoped to the primary color so coloured fills (secondary/error/…)
          // keep their solid backgrounds.
          "&.MuiButton-containedPrimary": {
            backgroundColor: "white",
            color: mobiliTheme.mainHeader,
            // Same reasoning as MuiChip: a white control on a light container
            // carries its own edge, the way MobiliCalendar's event blocks do.
            // Keeps the deliberate "light button, brand-blue label" decision
            // instead of flipping to a filled navy button.
            border: "1px solid var(--ink-edge)",
            boxShadow: "var(--elev-rest)",
            "&:hover": {
              backgroundColor: mobiliTheme.mainHeader,
              color: mobiliTheme.palette.secondary.contrastText,
              borderColor: mobiliTheme.mainHeader,
              boxShadow: "var(--elev-hover)",
            },
          },
          // Outlined buttons: brand-blue text/border on a transparent surface and,
          // unlike contained, they keep the dark text on hover (never white).
          "&.MuiButton-outlinedPrimary": {
            color: mobiliTheme.mainHeader,
            borderColor: mobiliTheme.mainHeader,
            "&:hover": {
              backgroundColor: "rgba(0, 48, 89, 0.08)",
              color: mobiliTheme.mainHeader,
              borderColor: mobiliTheme.mainHeader,
            },
          },
          '&:hover': { // Defines hover state styles
              backgroundColor: mobiliTheme.mainHeader, // Light background on hover
              color: mobiliTheme.palette.secondary.contrastText, // Dark text on hover
            },
          // Applying conditional styling based on the variant
          "&.MuiButton-text": {
            // This specifically targets buttons with the 'text' variant
            color: mobiliTheme.mainHeader,
            '&:hover': { // Defines hover state styles
              backgroundColor: mobiliTheme.mainHeader, // Light background on hover
              color: mobiliTheme.palette.secondary.contrastText, // Dark text on hover
            },            
          },
        },
      },
      // styleOverrides: {
      //   root: {
      //     backgroundColor: mobiliTheme.palette.primary.main,
      //     color:mobiliTheme.palette.primary.contrastText,
      //   },
      // },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.palette.secondary.main,
          color: "white",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background:
            process.env.REACT_APP_INSTANCE_NAME != "production"
              ?  `linear-gradient(to left, ${mobiliTheme.palette.secondary.main}, ${mobiliTheme.mainHeader})`
              : mobiliTheme.mainHeader,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: "0px", // Adjust this value as needed
          /**
           * White is the treatment for a chip with NO explicit colour.
           *
           * Scoped with :not() rather than applied to every chip. Unscoped it
           * also beat the palette background of `color="primary"` and friends
           * while MUI kept the matching contrastText label — which is white for
           * these palettes, so the label sat white-on-white and only became
           * readable once a hover background appeared behind it. Chips that ask
           * for a colour are now left entirely to MUI's own filled/outlined
           * handling.
           *
           * MUI 5 emits no `colorDefault` class (see Chip/chipClasses.js), so
           * "has no colour" has to be spelled out as the absence of the six.
           *
           * Deliberately NO border or shadow here.
           *
           * A white chip needs its own edge only when it sits on a CONTAINER
           * surface. On white content (a FlatCard body) the same treatment
           * turns what was effectively plain text into a drawn box — which is
           * the pills-everywhere problem, not a fix for it. A global chip
           * style cannot know which surface it landed on, so the edge belongs
           * to the usage: see ValueItem in molecules/FlexList.tsx.
           */
          "&:not(.MuiChip-colorPrimary):not(.MuiChip-colorSecondary):not(.MuiChip-colorError):not(.MuiChip-colorInfo):not(.MuiChip-colorSuccess):not(.MuiChip-colorWarning)":
            {
              backgroundColor: "white",
            },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: mobiliTheme.cardBackground,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.cardBackground,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.cardBackground,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        colorPrimary: {
          // This targets the specific state where colorPrimary is applied
          color: "green", // Replace '#yourColor' with the color you want
        },
        root: {
          // Targeting the root style for all InputLabels
          "&.Mui-focused": {
            color: mobiliTheme.palette.secondary.main, // Change 'red' to whatever color you want
          },
          "&.MuiFormLabel-filled": {
            color: mobiliTheme.mainHeader, // Change 'green' to whatever color you need when the label is filled
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "white", // Background color for text boxes
          // border: '2px solid black', // Border for text boxes
          borderRadius: lowLevelBorderRadius, // Border radius for text boxes
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.lowLevelContainerBackground, // Background color for autocomplete
          // border: '2px solid black', // Border for autocomplete
          borderRadius: lowLevelBorderRadius, // Border radius for autocomplete
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // backgroundColor: 'white', // Background color for menu items
          // border: '1px solid #ccc', // Border for menu items
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: mobiliTheme.palette.secondary.light, // Color for checked radio buttons
          },
          backgroundColor: "white", // Background color for radio buttons
          "&:hover": {
            backgroundColor: "rgba(0, 123, 255, 0.08)", // Background color on hover
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: "0 8px", // Custom margin for radio group labels
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'black', // Use secondary color for unchecked state
          '&.Mui-checked': {
            color: mobiliTheme.palette.secondary.light, // Use secondary color for checked state
          },
          // Must come after the .Mui-checked rule so disabled (incl. disabled +
          // checked) checkboxes fall back to the grey disabled color instead of red.
          '&.Mui-disabled': {
            color: mobiliTheme.palette.action.disabled,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'black', // Use secondary color for labels
          '&.Mui-focused': {
            color: mobiliTheme.mainHeader, // Use secondary color for focused labels
          },
          fontSize: "1rem", // Adjust to your desired size
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          width: "unset",
          // backgroundColor: '#f0f0f0', // Custom background color
          // color: '#333', // Custom text color
          // '&.MuiDataGrid-columnHeader--sortable': {
          //   cursor: 'pointer',
          //   '&:hover': {
          //     backgroundColor: '#e0e0e0', // Background color on hover
          //   },
          //   '&.MuiDataGrid-columnHeader--sorted': {
          //     backgroundColor: '#d0d0d0', // Background color when sorted
          //   },
          // },
        },
        columnHeaderTitle: {
          // fontWeight: 'bold', // Custom font weight
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: `${mobiliTheme.palette.secondary.main}`, // Use secondary color instead of primary
        },
        thumb: {
          color: `${mobiliTheme.palette.secondary.main}`, // Thumb color to secondary
        },
        track: {
          color: `${mobiliTheme.palette.secondary.main}`, // Track color to secondary
        },
        rail: {
          color: `${mobiliTheme.palette.secondary.main}`, // Rail color to secondary
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: mobiliTheme.lowLevelContainerBackground, // Set the custom background color for the table container
          borderRadius: "6px", 
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          margin: "4px", // Add margin to create space between cells
          fontSize: "0.7rem", // Set custom font size for this table
          padding: "4px", // Adjust padding if necessary
          backgroundColor:  "transparent", // Light gray background to mimic a chip
          // borderRadius: "16px", // Rounded corners like a chip
        },
        head: {
          fontSize: "0.85rem", // Font size for header cells
          fontWeight: "bold", // Make header cells bold
          backgroundColor: "transparent", // Set background color for header
          // Was #999, which is heavier than the system's primary rule. This is
          // --ink-line-strong, the same weight the calendar uses for hour lines.
          borderBottom: `1px solid ${inkLineStrong}`,
          borderRadius: "0px", // Rounded corners like a chip
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        body1: {
          fontSize: "0.875rem", // Customize the typography for table content
        },
      },
    },
    // MuiTableContainer: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: mobiliTheme.lowLevelContainerBackground, // Set the custom background color for the table container
    //     },
    //   },
    // },
    // MuiTableCell: {
    //   styleOverrides: {
    //     root: {
    //       fontSize: "0.7rem", // Set custom font size for this table
    //       padding: "1px", // Adjust padding if necessary          
    //       backgroundColor: "white", // Light gray background to mimic a chip
    //       borderRadius: "16px", // Rounded corners like a chip          
    //       border: "0px solid #ccc", // Optional: subtle border for the chip effect
    //     },
    //     head: {
    //       fontSize: "0.85rem", // Font size for header cells
    //       fontWeight: "bold", // Make header cells bold
    //       backgroundColor: mobiliTheme.lowLevelContainerBackground, // Set background color for header
    //       color: "#333", // Custom text color for header cells
    //       borderBottom: "0px solid #999", // Custom border for header
    //     },
    //   },
    // },

  },
});

// typography

mobiliTheme = createTheme(mobiliTheme, {
  typography: {
    // fontFamily is set on the FIRST createTheme call above — setting it here
    // would apply to theme.typography.fontFamily only and leave the variants
    // behind. See the FONT_STACK comment.
    // h5: {
    //   backgroundColor: mobiliTheme.palette.primary.light,
    // },
    // h6: {
    //   backgroundColor: mobiliTheme.palette.primary.light,
    // },
  },
});
// densify:
mobiliTheme = createTheme(mobiliTheme, {
  //const theme = createTheme({
  components: {
    MuiButton: {
      defaultProps: {
        size: "small",
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: mobiliTheme.lowLevelContainerBackground, // This is your off color, adjust as needed
          "&.Mui-checked": {
            color: mobiliTheme.palette.secondary.main, // This is your on color, adjust as needed
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: mobiliTheme.palette.secondary.light, // Track color when checked
          },
        },
        track: {
          // Was #767577. MUI's own default track is black at 0.38; this is the
          // same convention on the brand hue.
          backgroundColor: inkFill,
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiListItem: {
      defaultProps: {
        dense: true,
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiFab: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        size: "small",
        root: {
          backgroundColor: mobiliTheme.palette.secondary.main,
          color: "white",
        },
      },
    },

    MuiTable: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTextField: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiToolbar: {
      defaultProps: {
        variant: "dense",
      },
    },
  },
});

mobiliTheme.spacing(2);

// console.log("mobiliTheme.typography", mobiliTheme.typography);


export const sublocalityColors = [
  '#982f2f',
  '#e0e05c',
  '#4c6aac',
  '#ab5790',
  '#74d4db',
  '#4ab05a',
  '#2646a4',
  '#c67fbc',
  '#615332',
  '#be663b',
  '#519c79'
];
